import { useEffect } from "react";
import { useDispatch } from "react-redux";

/**
 * De-duplicated "resolve this reference for display" fetching, for the `Nice*` cell
 * components that turn an id into a name.
 *
 * These components render once per table row, and each one used to fetch independently
 * with a `if (!found) dispatch(...)` guard. Two problems followed from that:
 *
 *  - N rows produced N concurrent requests for the same handful of ids, and every
 *    response wrote to the store, re-rendering every other cell on the page.
 *  - The guard cannot tell "never fetched" from "fetched, and the answer was nothing".
 *    A missing or failed record therefore re-requested on every render, for as long as
 *    the table stayed open.
 *
 * A registry records which keys have been asked for, process-wide, so each id is fetched
 * once and a miss is never retried in a loop.
 *
 * @example
 *   const registry = createLookupRegistry();
 *   // inside the component:
 *   useLookupOnce(registry, id, (key) => getWorkflow({ id: key }), { resolved: !!workflow });
 */
export const createLookupRegistry = () => new Set();

/**
 * @param {Set}      registry  registry created by `createLookupRegistry`, module-level
 * @param {string}   key       the id/handle being resolved; falsy keys are skipped
 * @param {Function} action    `(key) => reduxAction` used to request the record
 * @param {object}   [options]
 * @param {boolean}  [options.resolved] true when the record is already in the store
 * @param {boolean}  [options.skip]     true to opt this render out entirely
 */
export const useLookupOnce = (registry, key, action, options = {}) => {
  const dispatch = useDispatch();
  const { resolved = false, skip = false } = options;

  useEffect(() => {
    if (skip || resolved || !key || registry.has(key)) {
      return;
    }

    registry.add(key);
    dispatch(action(key));
    // `action` is typically an inline arrow and is deliberately not a dependency;
    // `key` fully determines the request.
  }, [key, resolved, skip, dispatch]);
};

export default useLookupOnce;
